import React, { forwardRef, useEffect, useState, useCallback, useImperativeHandle, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import NumberInput from "../../components/NumberInput";
import {
  QUANTITY_UNITS,
  QUANTITY_UNIT_LABELS,
  availableUnits,
  canChooseUnit,
  conversionHint,
  packMetrics,
} from "./insulationQuantityUnits.js";
import GazNumberInput from "../../components/GazNumberInput";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import UniqueNomenclature from "./UniqueNomenclature";
import useProductsLogic from "./useProductsLogic";
import useAnalogs from "./useAnalogs";
import AnalogsList from "./AnalogsList";
import InsulationPicker from "./InsulationPicker";
import useInsulationPickerState from "./useInsulationPickerState";
import { useOptions } from "../../OptionsContext";
import ProductsSearch from "./ProductsSearch";
import { shouldUseStrictGazPalletUnits } from "../../utils/gazPalletRules";
import { isGazobetonProductRow } from "../../utils/mskGazobetonScope";
import { isCalcDebugEnabled } from "../../utils/calcDebug";
import { isSameNumericValue } from "../../utils/sameNumericValue";

import "../../theme/redesign/tokens.css";
import "../../theme/redesign/section.css";
import "../../theme/redesign/products.css";

// Предел строк «Товаров» — тот же, на который смотрит addRow в useProductsLogic.
const MAX_PRODUCT_ROWS = 20;

// Поля строки товара, которые пересчёт по нормам производства переписывает
// целиком. Сравниваются они порознь: числовые — по значению, остальные — как есть.
const SYNCED_NUMERIC_FIELDS = ["factVolume", "numberPallets", "itemsPerPallet", "palletUnits"];
const SYNCED_TEXT_FIELDS = ["manufacturer", "bitrixId", "source", "category", "product_group", "unit"];

// «40.32» и 40.32 — одно и то же количество: форматирование зависит от того, какой
// из пересчётов записал поле последним, а не от норм.
const isSameNumericField = isSameNumericValue;

const isSameSyncedProductState = (product, nextProduct) =>
  SYNCED_TEXT_FIELDS.every((field) => product[field] === nextProduct[field]) &&
  SYNCED_NUMERIC_FIELDS.every((field) => isSameNumericField(product[field], nextProduct[field]));

// Эффект рассеивания Таноса — строка распадается на пыль и исчезает
function spawnThanosDisintegration(row, callback) {
  const rect = row.getBoundingClientRect();
  if (!rect.width) { callback(); return; }

  const PARTICLE_COUNT = 1000;

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    setTimeout(() => {
      const el = document.createElement('div');
      const x = rect.left + Math.random() * rect.width;
      const y = rect.top + Math.random() * rect.height;
      const size = 1.5 + Math.random() * 5;

      // Разлёт во все стороны с небольшим сдвигом вправо-вверх
      const angle = (Math.random() - 0.5) * 2 * Math.PI;
      const power = 70 + Math.random() * 170;
      const dx = Math.cos(angle) * power + 25;
      const dy = Math.sin(angle) * power - 25;

      // Пепельно-розовые тона
      const r = 175 + Math.floor(Math.random() * 65);
      const g = 110 + Math.floor(Math.random() * 70);
      const b = 130 + Math.floor(Math.random() * 90);

      Object.assign(el.style, {
        position: 'fixed',
        left: x + 'px',
        top: y + 'px',
        width: size + 'px',
        height: size + 'px',
        borderRadius: Math.random() > 0.45 ? '50%' : '1px',
        background: `rgb(${r},${g},${b})`,
        pointerEvents: 'none',
        zIndex: '99999',
        willChange: 'transform, opacity',
      });

      document.body.appendChild(el);

      el.animate([
        { opacity: 1, transform: 'scale(1) translate(0,0)' },
        { opacity: 0.5, transform: `scale(0.7) translate(${dx * 0.35}px,${dy * 0.35}px)`, offset: 0.4 },
        { opacity: 0, transform: `scale(0)   translate(${dx}px,${dy}px)` },
      ], {
        duration: 550 + Math.random() * 400,
        easing: 'ease-out',
        fill: 'forwards',
      }).finished.then(() => el.remove());
    }, Math.random() * 220); // растянутый спаун
  }

  // 1. Быстро гасим строку (без изменения высоты — без прыжка)
  row.style.transition = 'opacity 0.18s ease-out';
  row.style.opacity = '0';

  // 2. Заменяем строку spacer-ом той же высоты, потом анимируем spacer → 0.
  //    Так мы обходим ненадёжное поведение height на <tr> (border-spacing, td-padding и пр.):
  //    React удаляет реальный узел сразу, а spacer — чистый DOM без React — схлопывается плавно.
  setTimeout(() => {
    const rowH  = row.offsetHeight;
    const colCount = row.querySelectorAll('td, th').length || 1;

    const spacer   = document.createElement('tr');
    const spacerTd = document.createElement('td');
    spacerTd.colSpan = colCount;
    // Transition НЕ включаем сразу — браузер должен сначала "закрепить" начальную высоту
    spacerTd.style.cssText = `height: ${rowH}px; padding: 0; border: none; overflow: hidden;`;
    spacer.appendChild(spacerTd);

    row.parentElement.insertBefore(spacer, row);

    // flushSync: заставляем React удалить строку синхронно прямо сейчас,
    // чтобы к моменту старта анимации строка уже не занимала место
    flushSync(() => callback());

    // Двойной RAF: первый — браузер фиксирует layout со spacer-ом;
    // второй — теперь безопасно включаем transition и схлопываем
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        spacerTd.style.transition = 'height 0.28s ease-in-out';
        spacerTd.style.height = '0';
        setTimeout(() => spacer.remove(), 300);
      });
    });
  }, 200);
}

// Склонение «позиция / позиции / позиций» под количество товаров.
const pluralizePositions = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} позиция`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} позиции`;
  return `${count} позиций`;
};

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  "бордюр",
  "бордюр дорожный",
  "брусчатка",
  "брусчатка и тротуарная плитка",
  "плитка тротуарная",
  "газонная решетка",
  "лоток водоотводный жби",
  "лоток водоотводный тротуарный",
]);

const NUMBER_PER_PALLET_CATEGORIES = new Set([
  "бордюр",
  "бордюр дорожный",
  "плитка тротуарная",
  "газонная решетка",
  "лоток водоотводный жби",
  "лоток водоотводный тротуарный",
]);

const isGazDeliveryCategory = (value, region) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.includes("газобетон")) {
    return true;
  }

  if (!GAZ_DELIVERY_CATEGORY_NAMES.has(normalized)) {
    return false;
  }

  return ["МСК", "КРД"].includes(normalizeRegionCode(region));
};

const normalizeRegionCode = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "МОСКВА" || normalized === "МОСКОВСКАЯ ОБЛАСТЬ") {
    return "МСК";
  }
  if (normalized === "САНКТ-ПЕТЕРБУРГ" || normalized === "ЛЕНИНГРАДСКАЯ ОБЛАСТЬ") {
    return "СПБ";
  }
  return normalized;
};

const isRegionalGikPriceSource = (source, region) => {
  const normalizedRegion = normalizeRegionCode(region);
  const normalizedSource = String(source || "").trim().toUpperCase();
  return ["МСК", "КРД"].includes(normalizedRegion) &&
    normalizedSource.includes(`ПРАЙС ГИК ${normalizedRegion}`);
};

const isRegionalGikPriceListSource = (source) => {
  const normalizedSource = String(source || "").trim().toUpperCase();
  return normalizedSource.includes("ПРАЙС ГИК МСК") || normalizedSource.includes("ПРАЙС ГИК КРД");
};

const Products = forwardRef(({
  products, setProducts, paymentMethod = "cash", region, isMskGazobetonScope = false, setData, productErrors, setProductErrors,
  addressAndCoordinates, selfPickUp, deliveryMethod,
}, ref) => {
  console.log('🔍 Products received paymentMethod:', paymentMethod);
  const { setOptions, optionsByPayment, setOptionsByPayment } = useOptions();

  // Выбираем правильный набор данных в зависимости способа оплаты
  const currentOptions = paymentMethod === 'card' ? optionsByPayment.noncash : optionsByPayment.cash;
  const defaultOptions = optionsByPayment.default;
  const options = currentOptions && Object.keys(currentOptions).length > 0 ? currentOptions : defaultOptions;

  // Логируем, какой набор данных используется. Обход словаря на тысячи позиций
  // ради счётчиков в логе шёл на каждый рендер «Товаров» — а рендерится секция
  // на каждый символ в поле количества. Оставляем это подробному режиму,
  // см. utils/calcDebug.
  if (isCalcDebugEnabled()) {
    console.log('🔍 Options selection:', {
      paymentMethod,
      currentOptionsKeys: Object.keys(currentOptions).length,
      defaultOptionsKeys: Object.keys(defaultOptions).length,
      usedOptions: options === currentOptions ? 'currentOptions' : 'defaultOptions',
      sampleKeys: Object.keys(options).slice(0, 3),
      isCardPayment: paymentMethod === 'card',
      optionsByPaymentState: {
        cashExists: !!optionsByPayment.cash && Object.keys(optionsByPayment.cash).length > 0,
        noncashExists: !!optionsByPayment.noncash && Object.keys(optionsByPayment.noncash).length > 0,
        defaultExists: !!optionsByPayment.default && Object.keys(optionsByPayment.default).length > 0,
        cashKeys: optionsByPayment.cash ? Object.keys(optionsByPayment.cash).length : 0,
        noncashKeys: optionsByPayment.noncash ? Object.keys(optionsByPayment.noncash).length : 0,
        defaultKeys: optionsByPayment.default ? Object.keys(optionsByPayment.default).length : 0
      }
    });

    // Дополнительно проверяем производителя для конкретного продукта
    const targetProduct = "Утеплитель Технониколь Роклайт 50 % компрессия 50 х 600 х 1200 мм (0,43 м3; 8,64 м2; 12 шт)";
    const cashData = optionsByPayment.cash && optionsByPayment.cash[targetProduct];
    const noncashData = optionsByPayment.noncash && optionsByPayment.noncash[targetProduct];

    if (cashData && noncashData) {
      console.log('🎯 Specific product comparison:', {
        paymentMethod,
        cashManufacturer: cashData.MANUFACTURER,
        noncashManufacturer: noncashData.MANUFACTURER,
        cashPrice: cashData.PRICE_CASH,
        noncashPrice: cashData.PRICE_NONCASH,
        currentDatasetManufacturer: options[targetProduct]?.MANUFACTURER,
        manufacturersDifferent: cashData.MANUFACTURER !== noncashData.MANUFACTURER
      });
    }
  }

  // Сохраняем предыдущие опции для отслеживания изменений
  const prevOptionsRef = useRef(options);

  const {
    addRow,
    deleteRow,
    handleDropdownChange,
    handlePriceChange,
    handleQuantityUnitInput,
    handleGazobetonPalletChange,
    handleGazobetonItemsPerPalletChange,
    calculateTotalWeight,
    calculateTotalVolume,
    shouldShowFactVolumeColumn,
    shouldShowMass,
    shouldShowVol,
    getProductRestrictions,
  } = useProductsLogic(products, setProducts, paymentMethod, options, region);

  const formatPalletValue = (value) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      return "-";
    }

    if (Number.isInteger(numericValue)) {
      return String(numericValue);
    }

    return numericValue.toFixed(2).replace(/\.?0+$/, "");
  };

  // Предоставляем функции через ref для использования в других компонентах
  useImperativeHandle(ref, () => ({
    deleteRow,
    addRow,
    // Функция для удаления материала по имени (для нерудки)
    deleteRowByName: (materialName) => {
      const product = products.find(p => p.name === materialName);
      if (product) {
        deleteRow(product.id);
      }
    }
  }), [deleteRow, addRow, products]);

  // Функция для безопасного округления только для отображения
  const formatForDisplay = (value) => {
    if (value === "-" || value === null || value === undefined) return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const rounded = num.toFixed(3).replace(/\.?0+$/, "");
    // Добавляем неразрывный пробел между тысячами для удобочитаемости.
    const [intPart, fracPart] = rounded.split(".");
    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return fracPart ? `${intWithSep}.${fracPart}` : intWithSep;
  };

  // Состояние для хранения cache.densityRanges
  const [densityRanges, setDensityRanges] = useState([]);
  const [izolifeSettings, setIzolifeSettings] = useState({ settings: [], deliveryCostByRegion: {}, truckPalletsByProduct: {} });
  const cache = useMemo(() => ({ densityRanges, izolifeSettings }), [densityRanges, izolifeSettings]); // Формируем объект cache для передачи в useAnalogs

  // Запрос данных densityRanges при монтировании компонента
  useEffect(() => {
    const fetchDensityRanges = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/density-ranges`);
        const result = await response.json();
        if (result.success) {
          setDensityRanges(result.data);
        } else {
          console.error('❌ Ошибка при загрузке densityRanges:', result.error);
          setDensityRanges([]);
        }
      } catch (error) {
        console.error('❌ Ошибка при запросе densityRanges:', error);
        setDensityRanges([]);
      }
    };

    const fetchIzolifeSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/izolife-settings`);
        const result = await response.json();
        if (result.success) {
          setIzolifeSettings({
            settings: result.settings || [],
            deliveryCostByRegion: result.deliveryCostByRegion || {},
            truckPalletsByProduct: result.truckPalletsByProduct || {},
          });
        } else {
          console.error('❌ Ошибка при загрузке izolifeSettings:', result.error);
        }
      } catch (error) {
        console.error('❌ Ошибка при запросе izolifeSettings:', error);
      }
    };

    fetchDensityRanges();
    fetchIzolifeSettings();
  }, []);

  const { getAnalogs } = useAnalogs(products, options, paymentMethod, cache);
  const [expandedRows, setExpandedRows] = useState({});
  const shouldShowTotalsFooter =
    (shouldShowMass && calculateTotalWeight) ||
    (shouldShowVol && calculateTotalVolume) ||
    (shouldShowFactVolumeColumn && calculateTotalVolume);

  const toggleRowExpansion = useCallback((index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  }, []);

  const handleSelectAnalog = useCallback(
    (analogValue, productId, quantity) => {
      const newQuantity = quantity ? quantity.toString() : '1';
      handlePriceChange(newQuantity, productId);
      handleDropdownChange(analogValue, productId);
      setExpandedRows({});
    },
    [handleDropdownChange, handlePriceChange]
  );

  // --- Умный подбор утеплителя ---------------------------------------------

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Отбор подбора держим здесь, а не внутри панели: «Скрыть подбор» убирает её
  // с экрана, и состояние внутри неё пропало бы вместе с ней. Менеджер,
  // свернувший подбор чтобы взглянуть на расчёт, терял бы все фильтры и
  // введённые количества.
  const pickerState = useInsulationPickerState();

  // Выбор из подбора ждёт свою строку здесь. Заполнить её сразу после addRow
  // нельзя: строка появится только со следующим состоянием products, а
  // handleDropdownChange работает по id уже существующей строки.
  const pendingPickRef = useRef(null);

  const firstEmptyRow = useCallback(
    (list) => list.find((product) => !String(product.name || "").trim()),
    []
  );

  const fillRowFromPicker = useCallback(
    (productId, name, quantity) => {
      handlePriceChange(String(quantity), productId);
      handleDropdownChange(name, productId);
    },
    [handleDropdownChange, handlePriceChange]
  );

  // Подбор пишет в первую незаполненную строку, а не добавляет новую поверх неё:
  // нажатие «Добавить товар» как раз и создаёт такую пустую строку, и оставлять
  // её висеть рядом с подставленным товаром незачем.
  const handlePickInsulation = useCallback(
    (row, quantity) => {
      const empty = firstEmptyRow(products);
      if (empty) {
        fillRowFromPicker(empty.id, row.name, quantity);
        return;
      }

      // На пределе в 20 строк addRow только покажет alert и ничего не добавит.
      // Записать выбор в ожидание здесь нельзя: он провисел бы до тех пор, пока
      // менеджер не удалит какую-нибудь строку, и подставился бы в неё сам —
      // спустя время и без всякой связи с нажатием.
      if (products.length >= MAX_PRODUCT_ROWS) {
        pendingPickRef.current = null;
        addRow();
        return;
      }

      pendingPickRef.current = { name: row.name, quantity };
      addRow();
    },
    [products, firstEmptyRow, fillRowFromPicker, addRow]
  );

  useEffect(() => {
    const pending = pendingPickRef.current;
    if (!pending) return;

    const empty = firstEmptyRow(products);
    if (!empty) return;

    pendingPickRef.current = null;
    fillRowFromPicker(empty.id, pending.name, pending.quantity);
  }, [products, firstEmptyRow, fillRowFromPicker]);

  // Обертка для сброса ошибки при выборе из dropdown 
  const handleDropdownChangeWithErrorReset = (value, id) => {
    handleDropdownChange(value, id);
    // Удаляем ошибку для этого товара, если она есть
    if (setProductErrors) {
      setProductErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  // Обертка для сброса ошибки при удалении товара
  const deleteRowWithErrorCleanup = (id) => {
    const doDelete = () => {
      deleteRow(id);
      if (setProductErrors) {
        setProductErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }
    };

    // Эффект рассеивания Таноса в кошко-режиме
    if (document.querySelector('.app-catgirl')) {
      const row = document.querySelector(`tr[data-product-id="${id}"]`);
      if (row) {
        spawnThanosDisintegration(row, doDelete);
        return;
      }
    }

    doDelete();
  };

  const getAnalogsForProduct = useCallback((productName, productQuantity) => {
    const analogsData = getAnalogs(productName, productQuantity);
    if (!analogsData || !analogsData.analogs) return { analogs: [] };
    const izolifeRaw = analogsData.analogs.filter(a => a.isIzolife);
    if (izolifeRaw.length > 0) {
      console.log(`🟡 [Изолайф Products] "${productName}" — Изолайф аналогов до фильтра volumeBenefit: ${izolifeRaw.length}`, izolifeRaw.map(a => ({ label: a.label, volumeBenefit: a.volumeBenefit, totalCost: a.totalCost })));
    }
    const filtered = analogsData.analogs.filter(analog => analog.isPavingAnalog || analog.isBrickAnalog || analog.volumeBenefit < 0);
    const izolifeAfter = filtered.filter(a => a.isIzolife);
    if (izolifeRaw.length > 0) {
      console.log(`🟡 [Изолайф Products] "${productName}" — Изолайф аналогов после фильтра volumeBenefit < 0: ${izolifeAfter.length}`);
    }
    return { analogs: filtered };
  }, [getAnalogs, paymentMethod]); // Добавляем paymentMethod в зависимости

  // Обновляем состояние expandedRows при изменении paymentMethod
  const [paymentMethodVersion, setPaymentMethodVersion] = useState(0);

  const isGazobetonCategory = (value) => {
    if (typeof value !== "string") {
      return false;
    }

    return isGazDeliveryCategory(value, region);
  };

  const isGazDeliveryProduct = (selectedData = {}, product = {}) => {
    const data = selectedData || {};
    const currentProduct = product || {};
    return (
      isGazDeliveryCategory(data.CATEGORY, region) ||
      isGazDeliveryCategory(data.CALCULATOR, region) ||
      isGazDeliveryCategory(currentProduct.product_group, region) ||
      isGazDeliveryCategory(currentProduct.category, region) ||
      isRegionalGikPriceSource(data.SOURCE || currentProduct.source, region)
    );
  };

  const normalizeOptionName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/ё/g, "е")
      .replace(/х/g, "x")
      .replace(/\s+/g, " ");

  // options — словарь на тысячи позиций (вся уникальная номенклатура).
  // Промах по точному имени раньше запускал Object.keys(options).find() —
  // полный проход с нормализацией каждого ключа на каждый вызов, а вызывается
  // findOptionByProductName в этом компоненте по несколько раз на товар за
  // рендер. Индекс строим один раз на изменение options.
  const normalizedOptionsIndex = useMemo(() => {
    const index = new Map();
    if (options) {
      Object.keys(options).forEach((key) => {
        const normalized = normalizeOptionName(key);
        if (!index.has(normalized)) {
          index.set(normalized, key);
        }
      });
    }
    return index;
  }, [options]);

  const findOptionByProductName = (name) => {
    if (!name || !options) {
      return null;
    }

    if (options[name]) {
      return options[name];
    }

    const matchedKey = normalizedOptionsIndex.get(normalizeOptionName(name));
    return matchedKey ? options[matchedKey] : null;
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsed = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isCubicMeterOptionUnit = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/³/g, "3")
      .replace(/\s+/g, "");

    return normalized === "м3" || normalized === "м.куб" || normalized === "мкуб";
  };

  const getUnitsPerPalletValue = (selectedData) => {
    const numberPerPallet = parseNumericValue(
      selectedData.number_per_pallet ??
      selectedData.NUMBER_PER_PALLET ??
      selectedData.UNITS_PER_PALLET
    );
    const category = String(selectedData.CATEGORY || "").trim().toLowerCase();
    const calculator = String(selectedData.CALCULATOR || "").trim().toLowerCase();
    const shouldUseOnlyNumberPerPallet = (
      NUMBER_PER_PALLET_CATEGORIES.has(category) ||
      NUMBER_PER_PALLET_CATEGORIES.has(calculator)
    ) && isRegionalGikPriceListSource(selectedData.SOURCE);

    if (shouldUseOnlyNumberPerPallet) {
      return numberPerPallet !== null && numberPerPallet > 0 ? numberPerPallet : "-";
    }

    const isCubicMeter = isCubicMeterOptionUnit(selectedData.BASE_UNIT);

    if (!isCubicMeter && numberPerPallet !== null && numberPerPallet > 0) {
      return numberPerPallet;
    }

    const palletUnits = parseNumericValue(selectedData.PALLET_UNITS);
    if (isCubicMeter) {
      if (palletUnits !== null && palletUnits > 0) {
        return palletUnits;
      }
      if (numberPerPallet !== null && numberPerPallet > 0) {
        return numberPerPallet;
      }
      return "-";
    }

    const unitsPerPallet = parseNumericValue(selectedData.UNITS_PER_PALLET);
    if (unitsPerPallet !== null && unitsPerPallet > 0) {
      return unitsPerPallet;
    }

    if (palletUnits !== null && palletUnits > 0) {
      return palletUnits;
    }
    if (numberPerPallet !== null && numberPerPallet > 0) {
      return numberPerPallet;
    }
    return "-";
  };

  const shouldUseStrictGazPalletUnitsForProduct = (selectedData = {}, product = {}) =>
    shouldUseStrictGazPalletUnits(
      selectedData.CATEGORY,
      selectedData.CALCULATOR,
      product.product_group,
      product.category
    );

  const getFactQuantityPerPalletValue = (selectedData, product = {}) => {
    if (shouldUseStrictGazPalletUnitsForProduct(selectedData, product)) {
      const strictPalletUnits = parseNumericValue(selectedData.PALLET_UNITS);
      if (strictPalletUnits !== null && strictPalletUnits > 0) {
        return strictPalletUnits;
      }
    }

    return getUnitsPerPalletValue(selectedData);
  };

  const getItemsPerPalletValue = (selectedData) => {
    const itemsPerPallet = parseNumericValue(
      selectedData.number_per_pallet ??
      selectedData.NUMBER_PER_PALLET ??
      selectedData.UNITS_PER_PALLET
    );

    return itemsPerPallet !== null && itemsPerPallet > 0 ? itemsPerPallet : null;
  };

  const getItemsByPallets = (selectedData, numberPallets, product = {}) => {
    const pallets = parseFloat(numberPallets);
    if (!Number.isFinite(pallets) || pallets <= 0) {
      return "-";
    }

    if (shouldUseStrictGazPalletUnitsForProduct(selectedData, product)) {
      const itemsPerPallet = getItemsPerPalletValue(selectedData);
      if (itemsPerPallet !== null && itemsPerPallet > 0) {
        return pallets * itemsPerPallet;
      }
    }

    const unitsPerPallet = getUnitsPerPalletValue(selectedData);
    if (!Number.isFinite(unitsPerPallet) || unitsPerPallet <= 0) {
      return "-";
    }

    return pallets * unitsPerPallet;
  };

  const hasGazobetonProducts = products.some((product) =>
    isGazDeliveryProduct(findOptionByProductName(product.name), product)
  );

  const normalizeUnit = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/³/g, "3")
      .replace(/\s+/g, "");

  const isCubicMeterUnit = (value) => {
    const normalized = normalizeUnit(value);
    return normalized === "м3" || normalized === "м.куб" || normalized === "мкуб";
  };

  const palletMultipleHeaderUnits = products
    .filter((product) => isGazDeliveryProduct(findOptionByProductName(product.name), product))
    .map((product) => String(product.unit || "").trim())
    .filter(Boolean);
  const uniquePalletMultipleHeaderUnits = [...new Set(palletMultipleHeaderUnits)];
  const palletMultipleHeaderUnit = uniquePalletMultipleHeaderUnits.length === 1
    ? uniquePalletMultipleHeaderUnits[0]
    : "Ед. изм.";

  const shouldShowItemsPerPalletColumn = shouldShowFactVolumeColumn && products.some((product) => (
    isGazDeliveryProduct(findOptionByProductName(product.name), product)
    && isCubicMeterUnit(product.unit)
  ));

  const productTableColumnCount =
    4
    + (shouldShowMass ? 1 : 0)
    + (shouldShowVol ? 1 : 0)
    + (shouldShowFactVolumeColumn ? 2 : 0)
    + (shouldShowItemsPerPalletColumn ? 1 : 0);

  const footerTrailingColumnCount =
    1
    + (shouldShowFactVolumeColumn ? 1 : 0)
    + (shouldShowItemsPerPalletColumn ? 1 : 0);

  // Обновляем товары при смене paymentMethod или при загрузке новых данных
  useEffect(() => {
    // Если опции изменились (загрузка новых данных), обновляем товары
    if (prevOptionsRef.current !== options) {
      console.log('🔄 Options changed, updating all products');

      // Увеличиваем версию для принудительного обновления
      setPaymentMethodVersion(prev => prev + 1);

      setProducts(prevProducts => {
        let hasChanges = false;
        const nextProducts = prevProducts.map(product => {
          if (!product.name || !options) return product;

          const selectedData = findOptionByProductName(product.name);
          if (!selectedData) return product;
          const isGazProduct = isGazDeliveryProduct(selectedData, product);
          const palletUnits = parseNumericValue(selectedData.PALLET_UNITS);
          const quantityPerPallet = getFactQuantityPerPalletValue(selectedData, product);
          const numberPallets = isGazProduct && quantityPerPallet
            ? (product._manualNumberPallets ? product.numberPallets : Math.ceil((parseNumericValue(product.quantity) || 0) / quantityPerPallet))
            : product.numberPallets;
          const factVolume = isGazProduct && quantityPerPallet
            ? numberPallets * quantityPerPallet
            : product.factVolume;
          const itemsPerPallet = isGazProduct
            ? (product._manualItemsPerPallet ? product.itemsPerPallet : getItemsByPallets(selectedData, numberPallets, product))
            : "-";

          // Обновляем данные товара из нового набора
          const nextProduct = {
            ...product,
            manufacturer: selectedData.MANUFACTURER || "Не указан",
            bitrixId: selectedData.BITRIX_ID || "",
            source: selectedData.SOURCE || "",
            category: selectedData.CALCULATOR || "-",
            product_group: selectedData.CATEGORY || "",
            unit: selectedData.BASE_UNIT || "-",
            factVolume,
            numberPallets,
            itemsPerPallet,
            palletUnits: isGazProduct
              ? (product._manualItemsPerPallet ? product.palletUnits : (quantityPerPallet ?? palletUnits ?? "-"))
              : "-",
            // Цена будет обновлена автоматически при отображении через useAnalogs
          };

          // Чаще всего пересчитывать нечего: options переписываются нормами
          // выбранного производства на каждый пересчёт сравнения, а сами нормы
          // при этом остаются прежними. Новый объект в такой момент менял ссылку
          // products, а за ней вхолостую шёл полный круг — запрос сравнения
          // производств, запрос способов доставки и пересборка расчёта заказа,
          // который на время пересборки прячется с экрана. Отдаём прежнюю строку,
          // пока ни одно из переписываемых полей не изменилось.
          if (isSameSyncedProductState(product, nextProduct)) {
            return product;
          }

          console.log(`🔄 Updating product ${product.name} for paymentMethod ${paymentMethod}`);
          hasChanges = true;
          return nextProduct;
        });

        return hasChanges ? nextProducts : prevProducts;
      });

      // Обновляем ref на новые опции
      prevOptionsRef.current = options;
    } else if (paymentMethod) {
      // при смене paymentMethod тоже увеличиваем версию
      setPaymentMethodVersion(prev => prev + 1);
    }
  }, [paymentMethod, options, setProducts]);

  // Отдельный эффект для обработки смены paymentMethod
  useEffect(() => {
    if (!prevOptionsRef.current || !options) return;

    console.log(`🔄 PaymentMethod changed to ${paymentMethod}, updating products`);

    setProducts(prevProducts => {
      let hasChanges = false;
      const nextProducts = prevProducts.map(product => {
        if (!product.name || !options) return product;

        const selectedData = findOptionByProductName(product.name);
        if (!selectedData) return product;
        const isGazProduct = isGazDeliveryProduct(selectedData, product);
        const palletUnits = parseNumericValue(selectedData.PALLET_UNITS);
        const quantityPerPallet = getFactQuantityPerPalletValue(selectedData, product);
        const numberPallets = isGazProduct && quantityPerPallet
          ? (product._manualNumberPallets ? product.numberPallets : Math.ceil((parseNumericValue(product.quantity) || 0) / quantityPerPallet))
          : product.numberPallets;
        const factVolume = isGazProduct && quantityPerPallet
          ? numberPallets * quantityPerPallet
          : product.factVolume;
        const itemsPerPallet = isGazProduct
          ? (product._manualItemsPerPallet ? product.itemsPerPallet : getItemsByPallets(selectedData, numberPallets, product))
          : "-";
        const nextPalletUnits = isGazProduct
          ? (product._manualItemsPerPallet ? product.palletUnits : (quantityPerPallet ?? palletUnits ?? "-"))
          : "-";

        // Проверяем, нужно ли обновить производителя
        const currentManufacturer = product.manufacturer;
        const newManufacturer = selectedData.MANUFACTURER || "Не указан";

        // Только обновляем если производитель действительно изменился
        if (currentManufacturer !== newManufacturer) {
          console.log(`🔄 Manufacturer changed from "${currentManufacturer}" to "${newManufacturer}" for ${product.name}`);

          hasChanges = true;
          return {
            ...product,
            manufacturer: newManufacturer,
            bitrixId: selectedData.BITRIX_ID || "",
            source: selectedData.SOURCE || "",
            category: selectedData.CALCULATOR || "-",
            product_group: selectedData.CATEGORY || "",
            unit: selectedData.BASE_UNIT || "-",
            factVolume,
            numberPallets,
            itemsPerPallet,
            palletUnits: nextPalletUnits,
          };
        }

        // Сравнение по значению, а не по типу: пересчёты записывают эти поля то
        // числом, то строкой из formatCalculatedValue, и строгое сравнение считало
        // строку изменением. Строка товара пересоздавалась на каждый пересчёт, а за
        // сменой ссылки products впустую уходил полный круг запросов.
        if (
          !isSameNumericField(product.itemsPerPallet, itemsPerPallet) ||
          !isSameNumericField(product.factVolume, factVolume) ||
          !isSameNumericField(product.numberPallets, numberPallets) ||
          !isSameNumericField(product.palletUnits, nextPalletUnits)
        ) {
          console.log(`🔄 Updating product ${product.name} for paymentMethod ${paymentMethod}`);
          hasChanges = true;
          return {
            ...product,
            factVolume,
            numberPallets,
            itemsPerPallet,
            palletUnits: nextPalletUnits,
          };
        }

        return product;
      });

      return hasChanges ? nextProducts : prevProducts;
    });
  }, [paymentMethod, setProducts]);

  // Подсчёт «осмысленных» товаров для подзаголовка секции.
  const meaningfulProductsCount = products.filter((p) => p && p.name).length;
  const sectionSubtitle = hasGazobetonProducts
    ? "газобетон · поддоны"
    : meaningfulProductsCount === 0
      ? "ничего не добавлено"
      : pluralizePositions(meaningfulProductsCount);

  const restrictions = getProductRestrictions;

  // Газобетон-режим:
  // isGazobetonView — полностью газобетон-режим: прячем Массу/Объём (не нужны),
  //                   но показываем явные столбцы «М³ кратно / Поддоны / Кол-во штук»
  //                   с редактируемыми полями (как в старой версии).
  const meaningfulProducts = products.filter((p) => p && p.name);
  const allMeaningfulAreGaz = meaningfulProducts.length > 0
    && meaningfulProducts.every((p) => isGazDeliveryProduct(findOptionByProductName(p.name), p));
  const isGazobetonView = hasGazobetonProducts && allMeaningfulAreGaz;
  // По требованию возвращаем явные газобетон-столбцы — не скрываем их.
  const hideGazColumns = false;

  const gridColumns = [
    "22px",                                              // №
    "minmax(160px, 1fr)",                               // Наименование
    "62px",                                             // Кол-во
    "48px",                                             // Ед. изм.
    !isGazobetonView && shouldShowMass ? "140px" : null, // Масса, т
    !isGazobetonView && shouldShowVol ? "170px" : null,  // Объём, м³
    shouldShowFactVolumeColumn ? "150px" : null,        // М³ кратно поддонам
    shouldShowFactVolumeColumn ? "110px" : null,        // Поддоны
    shouldShowItemsPerPalletColumn ? "170px" : null,    // Кол-во штук на поддонах
    "32px",                                             // Кнопка удаления
  ].filter(Boolean).join(" ");

  return (
    <section className="rd-section" ref={ref}>
      <UniqueNomenclature
        onDataLoaded={(data) => {
          if (data.cash && data.noncash && data.default) {
            setOptionsByPayment({
              cash: data.cash,
              noncash: data.noncash,
              default: data.default
            });
            // Устанавливаем значения по умолчанию
            setOptions(data.default);
          } else {
            // Для обратной совместимости
            setOptions(data);
          }
        }}
        region={region}
        setData={setData}
      />

      <div className="rd-sec-head">
        <h2>
          Товары
          <small>{sectionSubtitle}</small>
        </h2>
      </div>

      {hasGazobetonProducts && (
        <div className="rd-note">
          Для газобетона количество автоматически округляется до полных поддонов. Поэтому
          фактические м³ и штуки могут быть больше введённых.
        </div>
      )}

      <div className="rd-tovary-wrap">
        <table
          className="rd-tovary-table"
          style={{ "--rd-tovary-cols": gridColumns }}
        >
          <thead>
            <tr>
              <th scope="col">№</th>
              <th scope="col" className="rd-tovary-th--name">Наименование</th>
              <th scope="col">Кол-во</th>
              <th scope="col">Ед.</th>
              {!isGazobetonView && shouldShowMass && <th scope="col">Масса, т</th>}
              {!isGazobetonView && shouldShowVol && <th scope="col">Объём, м³</th>}
              {!hideGazColumns && shouldShowFactVolumeColumn && <th scope="col">{palletMultipleHeaderUnit} кратно</th>}
              {!hideGazColumns && shouldShowFactVolumeColumn && <th scope="col">Поддоны</th>}
              {!hideGazColumns && shouldShowItemsPerPalletColumn && <th scope="col">Количество штук на поддонах (кол.шт)</th>}
              <th scope="col" aria-label="Удалить"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => {
              const totalWeight =
                product.weight !== "-" && product.quantity
                  ? (parseFloat(product.weight) * parseFloat(product.quantity)).toFixed(2)
                  : "-";
              const totalVolume =
                product.volume !== "-" && product.quantity
                  ? (parseFloat(product.volume) * parseFloat(product.quantity)).toFixed(2)
                  : "-";

              const analogsResult = getAnalogsForProduct(product.name, product.quantity);
              const analogs = analogsResult.analogs;
              const analogsCount = analogs.length;
              const totalAnalogsCount = analogs.length;
              // Отладочная информация
              console.log(`🔍 Product ${product.name} - paymentMethod: ${paymentMethod}, expandedRows[${index}]: ${expandedRows[index]}, analogsCount: ${analogsCount}`);

              if (analogs.length > 0) {
                console.log(`🔍 Analogs for ${product.name}:`, analogs.map(a => ({
                  label: a.label,
                  cash: options[a.label]?.PRICE_CASH,
                  nonCash: options[a.label]?.PRICE_NONCASH,
                  currentPrice: a.details.price,
                  paymentMethod
                })));
              }
              const selectedData = findOptionByProductName(product.name);
              const isGazobetonProduct = isGazDeliveryProduct(selectedData, product);
              const shouldShowItemsPerPalletForProduct = isGazobetonProduct && isCubicMeterUnit(product.unit);

              return (
                <React.Fragment key={product.id}>
                  <tr data-product-id={product.id}>
                    <td>
                      <span className="rd-tovary-num">{index + 1}</span>
                    </td>
                    <td>
                      <div className="rd-tovary-name">
                        <div className="rd-tovary-name__search">
                          <ProductsSearch
                            id={`dropdown${product.id}`}
                            options={options}
                            selectedValue={product.name}
                            onChange={(value) => handleDropdownChangeWithErrorReset(value, product.id)}
                            restrictToNonmetallic={restrictions.shouldRestrictToNonmetallic}
                            excludeNonmetallic={restrictions.shouldExcludeNonmetallic}
                            paymentMethod={paymentMethod}
                          />
                        </div>
                        {totalAnalogsCount > 0 && (
                          <button
                            type="button"
                            className="rd-tovary-analogs-toggle"
                            onClick={() => toggleRowExpansion(index)}
                            aria-label={`Аналоги: ${totalAnalogsCount}`}
                          >
                            {expandedRows[index] ? <MdExpandLess size={14} /> : <MdExpandMore size={14} />}
                            <span>{totalAnalogsCount}</span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="qty-cell">
                      {(() => {
                        // Утеплитель считают в квадратах, а продают упаковками.
                        // Переключатель появляется только там, где меру есть из
                        // чего посчитать; у всего остального поле прежнее.
                        const canPickUnit = canChooseUnit(product.name, selectedData);
                        const unit = product._quantityUnit || QUANTITY_UNITS.PACK;
                        const metrics = canPickUnit ? packMetrics(product.name, selectedData) : null;
                        const hint = canPickUnit ? conversionHint(product._quantityInput, unit, metrics) : null;
                        const inPacks = !canPickUnit || unit === QUANTITY_UNITS.PACK;

                        return (
                          <>
                            <NumberInput
                              value={
                                inPacks
                                  ? (product.quantity === null || product.quantity === 1 ? "" : product.quantity)
                                  : (product._quantityInput || "")
                              }
                              onChange={(value) => (
                                inPacks
                                  ? handlePriceChange(value, product.id)
                                  : handleQuantityUnitInput(value, unit, product.id, metrics)
                              )}
                              allowDecimal={!inPacks || isGazDeliveryProduct(selectedData, product)}
                              maxDecimals={2}
                            />

                            {canPickUnit && (
                              <div className="qty-units" role="group" aria-label="Единица ввода количества">
                                {availableUnits(metrics).map((value) => (
                                  <button
                                    key={value}
                                    type="button"
                                    className={`qty-unit${unit === value ? " is-active" : ""}`}
                                    aria-pressed={unit === value}
                                    onClick={() => handleQuantityUnitInput(
                                      value === QUANTITY_UNITS.PACK ? "" : (product._quantityInput || ""),
                                      value,
                                      product.id,
                                      metrics
                                    )}
                                  >
                                    {QUANTITY_UNIT_LABELS[value]}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* Упаковка целая, поэтому количество округляется
                                вверх. Перебор менеджер должен увидеть здесь, а
                                не узнать от клиента после отправки КП. */}
                            {hint && <span className="qty-hint">{hint.text}</span>}
                          </>
                        );
                      })()}
                    </td>
                    <td className="rd-tovary-unit">{product.unit}</td>
                    {!isGazobetonView && shouldShowMass && (
                      <td className="rd-tovary-num" title={product.weight !== "-" ? `За 1 ${product.unit || "ед."}: ${formatForDisplay(product.weight)} т` : ""}>
                        {totalWeight !== "-"
                          ? formatForDisplay(totalWeight)
                          : (product.weight !== "-" ? formatForDisplay(product.weight) : "-")
                        }
                      </td>
                    )}
                    {!isGazobetonView && shouldShowVol && (
                      <td className="rd-tovary-num" title={product.volume !== "-" ? `За 1 ${product.unit || "ед."}: ${formatForDisplay(product.volume)} м³` : ""}>
                        {totalVolume !== "-"
                          ? formatForDisplay(totalVolume)
                          : (product.volume !== "-" ? formatForDisplay(product.volume) : "-")
                        }
                      </td>
                    )}
                    {!hideGazColumns && shouldShowFactVolumeColumn && (
                      <td className="rd-tovary-num">
                        {!isNaN(parseFloat(product.factVolume)) ? formatForDisplay(product.factVolume) : "-"}
                        {/* Пояснение об округлении — только внутри МСК Газобетона и только
                            у самой газобетонной позиции. Колонка включается для всей
                            группы доставки газобетона и для любой позиции с factVolume,
                            поэтому здесь проверяется фактическая категория строки. */}
                        {isMskGazobetonScope && isGazobetonProductRow(product) && (() => {
                          const entered = parseFloat(totalVolume !== "-" ? totalVolume : product.volume);
                          const fact = parseFloat(product.factVolume);
                          if (isNaN(entered) || isNaN(fact) || Math.abs(fact - entered) < 0.001) {
                            return null;
                          }
                          return (
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 11,
                                lineHeight: 1.3,
                                color: '#78909c',
                                fontWeight: 400,
                                whiteSpace: 'normal',
                              }}
                            >
                              Введено {formatForDisplay(entered)} — округлено до целого поддона
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {!hideGazColumns && shouldShowFactVolumeColumn && (
                      <td className="pallets-cell">
                        {isGazobetonProduct ? (
                          <GazNumberInput
                            value={product.numberPallets === "-" ? "" : product.numberPallets}
                            onChange={(value) => handleGazobetonPalletChange(value, product.id)}
                          />
                        ) : (!isNaN(Math.ceil(product.numberPallets)) ? Math.ceil(product.numberPallets) : "-")}
                      </td>
                    )}
                    {!hideGazColumns && shouldShowItemsPerPalletColumn && (
                      <td className="items-cell">
                        {shouldShowItemsPerPalletForProduct ? (
                          <GazNumberInput
                            value={product.itemsPerPallet === "-" ? "" : product.itemsPerPallet}
                            onChange={(value) => handleGazobetonItemsPerPalletChange(value, product.id)}
                          />
                        ) : "-"}
                      </td>
                    )}
                    <td>
                      <button
                        type="button"
                        onClick={() => deleteRowWithErrorCleanup(product.id)}
                        className="rd-tovary-del"
                        disabled={products.length === 1}
                        aria-label="Удалить позицию"
                        title="Удалить позицию"
                      />
                    </td>
                  </tr>
                  {productErrors && productErrors[product.id] && (
                    <div className="address-block__status address-block__status--error" style={{
                      marginLeft: '45px',
                      marginBottom: '8px'
                    }}>
                      {productErrors[product.id]}
                    </div>
                  )}

                  {expandedRows[index] && totalAnalogsCount > 0 && (
                    <AnalogsList
                      items={analogs}
                      onSelectAnalog={handleSelectAnalog}
                      colSpan={productTableColumnCount}
                      setExpandedRows={setExpandedRows}
                      currentProduct={product}
                      options={options}
                      paymentMethod={paymentMethod}
                      forceUpdate={paymentMethodVersion}
                      addressAndCoordinates={addressAndCoordinates}
                      selectedRegion={region}
                      selfPickUp={selfPickUp}
                      deliveryMethod={deliveryMethod}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {!isGazobetonView && shouldShowTotalsFooter && (
            <tfoot>
              <tr>
                <td style={{ gridColumn: '1 / 5', textAlign: 'right' }}>Общее:</td>
                {shouldShowMass && <td>{calculateTotalWeight !== null && calculateTotalWeight !== undefined ? formatForDisplay(calculateTotalWeight) : ""}</td>}
                {shouldShowVol && <td>{calculateTotalVolume !== null && calculateTotalVolume !== undefined ? formatForDisplay(calculateTotalVolume) : ""}</td>}
                {!hideGazColumns && shouldShowFactVolumeColumn && <td>{calculateTotalVolume !== null && calculateTotalVolume !== undefined ? formatForDisplay(calculateTotalVolume) : ""}</td>}
                {!hideGazColumns && shouldShowFactVolumeColumn && <td></td>}
                {!hideGazColumns && shouldShowItemsPerPalletColumn && <td></td>}
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="rd-add-row-center rd-add-row-center--pair">
        <button
          type="button"
          onClick={addRow}
          className="rd-add-btn"
          disabled={products.length >= MAX_PRODUCT_ROWS}
        >
          Добавить товар
        </button>

        {/* Подбор не заменяет «Добавить товар», а стоит рядом: это разные пути
            к одной строке заказа — вручную по названию или сравнением цены за куб. */}
        <button
          type="button"
          onClick={() => setIsPickerOpen((open) => !open)}
          className="rd-add-btn rd-add-btn--ghost"
          aria-expanded={isPickerOpen}
          disabled={products.length >= MAX_PRODUCT_ROWS}
        >
          {isPickerOpen ? "Скрыть подбор утеплителя" : "Умный подбор утеплителя"}
        </button>
      </div>

      {isPickerOpen && (
        <InsulationPicker
          paymentMethod={paymentMethod}
          options={options}
          state={pickerState}
          onSelect={handlePickInsulation}
          onClose={() => setIsPickerOpen(false)}
        />
      )}
    </section>
  );
});

export default Products;
